/**
 * The client half of the commit–reveal, which until now did not exist.
 *
 * The server side has always been complete, so every hand in production was
 * dealt from `["server","server","server","server"]` — entirely server-chosen
 * entropy — while the UI said the shuffle was verified. This hook is what makes
 * a real player's 32 bytes part of the deal.
 *
 * Order matters and is the whole point:
 *   1. a `committing` snapshot arrives carrying the commitments;
 *   2. they are written to local storage (`record.ts`);
 *   3. only then is a seed generated, stored, and sent.
 *
 * Doing (3) before (2) would leave a player who reloads unable to prove the
 * seed was theirs, and comparing against anything the server sends later proves
 * nothing at all.
 */
import { useEffect } from 'react'
import { CONTRIBUTION_PENDING, ClientEvent, randomSeedHex, type TableSnapshot } from '@cards/shared'
import { getSocket } from '../socket/socket'
import { noteContribution, observeCommitment } from './record'

/** Hands this tab has already emitted for, keyed by socket id as well, so a
 *  burst of snapshots between the emit and the server's acknowledgement does not
 *  spend four events out of the socket's budget — while a reconnect inside the
 *  seed window still gets one more go, because a dropped emit is not retried by
 *  socket.io any more (the send buffer is cleared on disconnect). */
const emitted = new Set<string>()

export function useFairness(snapshot: TableSnapshot | null): void {
  useEffect(() => {
    const match = snapshot?.match
    const hand = match?.hand
    if (!match || !hand) return

    const mySeat = snapshot?.you.seat ?? null
    // Recorded for every hand, including ones this device only spectates: the
    // commitment has to be captured before the reveal can exist, and there is
    // no second chance at it.
    const record = observeCommitment(
      match.matchId,
      hand.index,
      hand.fairness.commitment,
      hand.fairness.seatCommitments,
      mySeat,
    )

    if (hand.phase !== 'committing' || mySeat === null) return
    // The server has already recorded something for this seat — our own seed if
    // it arrived, or a fill if the window closed. Either way, nothing to send.
    if (hand.fairness.contributions[mySeat] !== CONTRIBUTION_PENDING) return

    const socket = getSocket()
    const key = `${match.matchId}:${hand.index}:${socket.id ?? 'pending'}`
    if (emitted.has(key)) return

    // After a reload mid-`committing` we send the *same* seed again rather than
    // a fresh one: the stored one is the only one we can later prove was ours.
    const seed = record.mySeed ?? randomSeedHex()
    noteContribution(match.matchId, hand.index, mySeat, seed)
    emitted.add(key)
    if (emitted.size > 64) emitted.delete(emitted.values().next().value as string)
    socket.emit(ClientEvent.ContributeSeed, { handIndex: hand.index, seed })
  }, [snapshot])
}
